import {
  PAYMODE,
  TOKEN,
  FLOW,
  EMI_SUBVENTION_STRATEGY,
  EMI_SUBVENTION_ITEM_KEYS
} from '../../../common/src/constant';
import { has, isNull } from '../../../common/src/utils/common';
import { locale } from '../utils/i18n';
import CONSTANT from '../utils/constants';
import { EVENT, log } from './logger';

/**
 * Recursive merger helper function
 * @param {Object} current - existing object
 * @param {Object} newObject - new object
 */

const recursiveMerge = (current, newObject) => {
  if (Object.keys(current).length === 0) {
    return newObject;
  }
  for (const prop in current) {
    if (Object.prototype.hasOwnProperty.call(current, prop)) {
      // key exists in source object
      if (Object.prototype.hasOwnProperty.call(newObject, prop)) {
        // key exists in target object
        if (current[prop] && current[prop].constructor === Object) {
          newObject[prop] = recursiveMerge(current[prop], newObject[prop]);
        }
      } else {
        // key not exists in target object
        // eslint-disable-next-line no-lonely-if
        if (current[prop] && current[prop].constructor === Object) {
          newObject[prop] = {};
          newObject[prop] = recursiveMerge(current[prop], newObject[prop]);
        } else {
          newObject[prop] = current[prop];
        }
      }
    }
  }
  return newObject;
};

/**
 * A private method to merge new Object with existing
 *
 * @param {Object} current - existing Object
 * @param {Object} newObject - new new Object
 * @returns {Object} - final merged object
 */
const mergeObject = (current = {}, newObject) => recursiveMerge(current, newObject);

const errorMessages = {
  merchant: {
    obj: 'merchant object is mandatory field in configuration',
    mid: 'merchant id is mandatory field in merchant object of configuration',
    webviewConsent: 'multipleWindowWebview key should be of boolean type',
    type: 'merchant id should be of string type',
    notifyMerchantCallbackMandatory:
      'notifyMerchant function handler is mandatory in handler object',
    notifyMerchantCallbackFunction: 'notifyMerchant should be of function type',
    transactionStatus:
      'transactionStatus needs to be implemented if redirect flag set to false in merchant object'
  },
  data: {
    obj: 'data object is mandatory field in configuration',
    token: 'if token is present then tokenType key should be there',
    tokenType: `token type should be one of the following only , `,
    amount: 'amount should be present on data object',
    orderId: 'order id should be present on data object',
    mandatoryToken: 'Token is mandatory'
  },
  flow: {
    deferredHandler: `For ${FLOW.DEFERRED} flow deferred function is mandatory in handler object`,
    tokenType: `For ${FLOW.DEFERRED} flow tokenType should be ${TOKEN.SSO} only`,
    txnTokenType: `For ${FLOW.DEFAULT} flow tokenType should be ${TOKEN.TXN} only`
  },
  token: {
    tokenHandler: `For ${TOKEN.ACCESS}  Initiate transaction function is mandatory in handler object`,
    emiSubventionObject: `For ${TOKEN.ACCESS}  emiSubvention key is mandatory in merchant config`,
    strategyRequired: `For emiSubvention object strategy key is required`,
    strategyType: `For emiSubvention strategy should be ${EMI_SUBVENTION_STRATEGY.ITEM_BASED} or ${EMI_SUBVENTION_STRATEGY.AMOUNT_BASED} only`,
    referenceId: `For Access Token Reference Id is mandatory`,
    noAmount: `For Amount based strategy, subventionAmount key is required`,
    noItems: `For Item based strategy, items object is required`,
    itemsKey: `For Item based strategy, items object  requires either of BrandID, CategoryList, Model or Ean`
  },
  handler: {
    initiateTransaction: `initiateTransaction method should be implemented if tokenType is ${TOKEN.GUEST}`
  }
};

const merchantMapClientMessage = () => {
  const mapping = {}; /* mapping of display text and client side validation error messages */
  mapping[CONSTANT.STATIC.toLowerCase()] = {
    label: {
      paymodeSelection: locale('static.paymode.select.option'),
      subscriptionSelection: locale('static.select.option.to.pay.subscription'),
      paymodeOtherSelection: locale('static.paymode.select.other'),
      saveCardSelection: locale('static.sc.select')
    },
    error: {}
  };
  mapping[CONSTANT.HEADER.toLowerCase()] = {
    label: {
      otpSend: locale('header.otp.send'),
      orderID: locale('header.order.id'),
      account: locale('header.account'),
      switch: locale('header.switch'),
      selectOption: locale('header.select.option.pay'),
      subscriptionOption: locale('header.select.option.to.pay.subscription'),
      viewSubsDetail: locale('subscription.viewDetails')
    },
    error: {}
  };

  mapping[CONSTANT.FOOTER.toLowerCase()] = {
    label: {
      message: locale('footer.message')
    },
    error: {}
  };

  mapping[CONSTANT.OFFERS.toLowerCase()] = {
    label: {
      message: locale('offers.message')
    },
    error: {}
  };

  mapping[CONSTANT.PROCESSING.toLowerCase()] = {
    label: {
      heading: locale('processing.heading'),
      info: locale('processing.info')
    },
    error: {}
  };

  mapping[CONSTANT.RETRY.toLowerCase()] = {
    label: {
      paymentFailed: locale('retry.payment.failed'),
      info: locale('retry.info')
    },
    error: {}
  };

  mapping[PAYMODE.CARD.toLowerCase()] = {
    label: {
      cardNumber: locale('card.number'),
      expiry: locale('card.expiry'),
      cvv: locale('card.cvv'),
      savedCard: locale('card.save.card')
    },
    error: {
      invalidCardNumber: locale('card.invalid.card.number'),
      invalidExpiry: locale('card.invalid.expiry'),
      invalidCvv: locale('card.invalid.cvv')
    }
  };

  mapping[PAYMODE.PAYMENT_BANK.toLowerCase()] = {
    label: {
      checkBalance: locale('ppbl.check.balance'),
      availableBalance: locale('ppbl.available.balance'),
      insufficientBalance: locale('ppbl.insufficient.balance'),
      enterPasscode: locale('ppbl.enterPasscode')
    },
    error: {
      invalidPasscode: locale('ppbl.invalid.passcode')
    }
  };

  mapping[PAYMODE.POSTPAID.toLowerCase()] = {
    label: {
      checkBalance: locale('pdc.check.balance'),
      availableBalance: locale('pdc.available.balance'),
      insufficientBalance: locale('pdc.insufficient.balance')
    }
  };

  mapping[PAYMODE.UPI.toLowerCase()] = {
    label: {
      selectUpi: locale('upi.select.upi'),
      upiEnterSuggest: locale('upi.handler.suggestion')
    },
    error: {
      required: locale('upi.id.required'),
      inValidId: locale('upi.invalid.id'),
      subscriptionError: locale('upi.subscription.error')
    }
  };

  mapping[PAYMODE.COD.toLowerCase()] = {
    label: {}
  };

  mapping[PAYMODE.NB.toLowerCase()] = {
    label: {
      secureRedirectionMessage: locale('nb.secure.redirection.message')
    }
  };

  mapping[PAYMODE.WALLET.toLowerCase()] = {
    label: {
      insufficientBalance: locale('wallet.insufficient.balance')
    }
  };

  mapping[PAYMODE.GIFT_VOUCHER.toLowerCase()] = {
    label: {
      insufficientBalance: locale('mgv.insufficient.balance')
    }
  };

  mapping[CONSTANT.LOGIN.toLowerCase()] = {
    label: {
      labelMessage: locale('login.message'),
      otpSentNumberMessage: locale('login.otp.sent.number'),
      requestOtp: locale('login.request.otp'),
      otpNotreceived: locale('login.otp.not.received'),
      rememberMe: locale('login.rememberMe'),
      btnSendOtpText: locale('login.btn.sendOtp.text'),
      btnVerifyOtpText: locale('login.btn.verifyOtp.text'),
      subscriptionMsg: locale('login.subscription.msg'),
      mobileNumber: locale('login.mobile.number'),
      changeNumber: locale('login.change.number')
    },
    error: {
      invalidOtpLength: locale('login.invalid.otp.length'),
      invalidMobileInput: locale('login.invalid.mobile.input')
    }
  };
  mapping[CONSTANT.EMI.toLowerCase()] = {
    label: {
      interest: locale('emi.interest'),
      emiConvert: locale('emi.emiConvert'),
      cashback: locale('emi.cashback'),
      kycText: locale('emi.kycText'),
      emiHybridDisabledText: locale('emi.emiHybridDisabledText'),
      emiHybridDisabledCardText: locale('emi.emiHybridDisabledCardText')
    },
    error: {
      errorBtn: locale('emiErrors.errorBtn'),
      emiBankListErrorHeading: locale('emiErrors.emiBankListErrorHeading'),
      emiBankListErrorSubtext: locale('emiErrors.emiBankListErrorSubtext'),
      emiPlanError: locale('emiErrors.emiPlanError'),
      emiPlanSubtext: locale('emiErrors.emiPlanSubtext'),
      emiPlanSelectError: locale('emiErrors.emiPlanSelectError'),
      emiNotAvailable: locale('emiErrors.emiNotAvailable')
    }
  };
  mapping[CONSTANT.SUBSCRIPTION.toLowerCase()] = {
    label: {
      savedCard: locale('subscription.savedCard'),
      details: locale('subscription.details'),
      upto: locale('subscription.upto'),
      maybe: locale('subscription.may.be.deducted'),
      sharedBy: locale('subscription.merchant.shared'),
      renewMessage: ''
    },
    error: {}
  };
  return mapping;
};

const mapStyle = () =>
  // merchant customization of style
  ({
    borderColor: '' /* border and input border and box color */,
    bodyBackgroundColor: '' /* background body color */,
    bodyColor: '' /* body color */,
    themeBackgroundColor: '' /* background color to be applied on pay button, checkbox, radio */,
    themeColor: '' /* text color to be applied across checkout payment page */,
    headerBackgroundColor: '' /* header background color */,
    headerColor: '' /* header text color */,
    errorColor: '' /* client side validation error message color */,
    successColor: '' /* client side validation success message color */,
    infoColor: '' /* client side validation info message color */,
    infoBackgroundColor: '' /* client side validation info message background color */,
    gradient: '' /* gradient object */
  });

const mapPayMode = () => {
  const mapping = {
    labels: {},
    filter: {
      include: [], // paymode to include in final render on user browser
      exclude: [PAYMODE.EMI, PAYMODE.PAYMENT_BANK] // paymode to remove from final render on user browser
    },
    order: [PAYMODE.NB, PAYMODE.CARD] // Ordering of paymodes as per merchant preference
  };

  // map paymode name as per merchant preferences
  mapping.labels[PAYMODE.NB] = 'Net Banking';
  mapping.labels[PAYMODE.CARD] = 'Some new Card';
  return mapping;
};

const mapLabels = () =>
  /* Textual changes for component text - button text , */
  ({
    pay: ''
  });
const mapMerchantProperties = () => ({
  /* merchant name and logo configuration */
  mid: '' /* merchant id */,
  name: '',
  logo: '',
  redirect: true /* when set user will redirect to callback url pass by merchant */,
  callbackUrl: '' /* callback url for */,
  hidePaytmBranding: false /* hide paytm branding - footer and header related to paytm */,
  multipleWindowWebview: false /* consent for multiple windows/tabs opening in web view */
});

const mapHandler = () => ({});

let _config = {
  root: '' /* querySelector to fetch using id or class name | DOM HTML node instance */,
  style: mapStyle(),
  flow: FLOW.DEFAULT /* Type of flow to invoke checkout - DEFAULT,DEFERRED */,
  data: {
    /* mandatory field provided by the merchant if these changes not provide then error thrown to merchant */
    orderId: '' /* order id */,
    token: '' /* token for using API */,
    tokenType: TOKEN.GUEST /* token type */,
    amount: '' /* transaction amount as string or number */,
    userDetail: {
      mobileNumber: '',
      name: ''
    }
  },
  merchant: mapMerchantProperties(),
  mapClientMessage: merchantMapClientMessage(),
  labels: mapLabels(),
  expireDuration: 3600000 /* window popup close after time duration expired */,
  payMode: mapPayMode(),
  handler: mapHandler(),
  hidePaymodeLabel: true,
  isGradientEffectEnable: false
};

const validations = {
  handler: function handlerValidations(config) {
    // handler callback related validations
    const output = {
      hasError: false,
      message: ''
    };

    // notifyMerchant is mandatory callback needs to implemented. It's used when payment page session expire occur
    if (!has(config.handler, 'notifyMerchant')) {
      output.hasError = true;
      output.message = errorMessages.merchant.notifyMerchantCallbackMandatory;
      return output;
    }

    // notifyMerchant should be of function type
    if (config.handler.notifyMerchant.constructor !== Function) {
      output.hasError = true;
      output.message = errorMessages.merchant.notifyMerchantCallbackFunction;
      return output;
    }

    const notifyMerchantFn = config.handler.notifyMerchant;
    config.handler.notifyMerchant = (eventName, data) => {
      log({ eventType: CONSTANT.NOTIFY_MERCHANT, eventName, data });
      notifyMerchantFn(eventName, data);
    };
    return output;
  },
  merchant: function merchantValidations(config) {
    // merchant related validations
    const output = {
      hasError: false,
      message: ''
    };

    // merchant object is not present
    if (!has(config, 'merchant')) {
      output.hasError = true;
      output.message = errorMessages.merchant.obj;
      return output;
    }

    // mid is not present
    if (!has(config.merchant, 'mid')) {
      output.hasError = true;
      output.message = errorMessages.merchant.mid;
      return output;
    }

    // multipleWindowWebview key should be of boolean type
    if (typeof config.merchant.multipleWindowWebview !== 'boolean') {
      output.hasError = true;
      output.message = errorMessages.merchant.webviewConsent;
      return output;
    }

    // mid should be of string type
    if (isNull(config.merchant.mid) || typeof config.merchant.mid !== 'string') {
      output.hasError = true;
      output.message = errorMessages.merchant.type;
      return output;
    }

    // transactionStatus needs to be implemented if redirect flag set to false in merchant object
    if (config.merchant.redirect === false && !has(config.handler, 'transactionStatus')) {
      output.hasError = true;
      output.message = errorMessages.merchant.transactionStatus;
      return output;
    }

    return output;
  },
  data: function dataValidations(config) {
    // transaction data related validations
    const output = {
      hasError: false,
      message: ''
    };

    // data object is not present
    if (!has(config, 'data')) {
      output.hasError = true;
      output.message = errorMessages.data.obj;
      return output;
    }

    //  token is mandatory
    if (has(config.data, 'token') && !config.data.token) {
      output.hasError = true;
      output.message = errorMessages.data.mandatoryToken;
      return output;
    }

    // if token is present then tokenType should be present
    if (has(config.data, 'token') && !has(config.data, 'tokenType')) {
      output.hasError = true;
      output.message = errorMessages.data.token;
      return output;
    }

    // transaction amount is mandatory field in default flow , in deferred flow amount is used from cart api response
    if (config.flow === FLOW.DEFAULT && !config.data.amount) {
      output.hasError = true;
      output.message = errorMessages.data.amount;
      return output;
    }

    // order id is mandatory field
    if (!config.data.orderId && config.data.tokenType && config.data.tokenType === TOKEN.TXN) {
      output.hasError = true;
      output.message = errorMessages.data.orderId;
      return output;
    }

    return output;
  },
  flow: function flowValidations(config) {
    // flow related validations
    const output = {
      hasError: false,
      message: ''
    };

    // if flow is DEFERRED then deferred callback is mandatory in handler
    if (config.flow === FLOW.DEFERRED && !has(config.handler, 'deferred')) {
      output.hasError = true;
      output.message = errorMessages.flow.deferredHandler;
      return output;
    }

    // if flow is DEFAULT then tokenType should of TXN_TOKEN
    if (
      config.flow === FLOW.DEFAULT &&
      (!has(config.data, 'tokenType') ||
        !has(config.data, 'token') ||
        (config.data.tokenType !== TOKEN.TXN && config.data.tokenType !== TOKEN.ACCESS))
    ) {
      output.hasError = true;
      output.message = errorMessages.flow.txnTokenType;
      return output;
    }

    // if flow is DEFERRED then tokenType should of SSO and token is always present
    if (
      config.flow === FLOW.DEFERRED &&
      (!has(config.data, 'tokenType') ||
        !has(config.data, 'token') ||
        config.data.tokenType !== TOKEN.SSO)
    ) {
      output.hasError = true;
      output.message = errorMessages.flow.tokenType;
      return output;
    }
    return output;
  },
  token: function tokenValidation(config) {
    // token related validations
    const output = {
      hasError: false,
      message: ''
    };
    const accessTokenExists =
      has(config.data, 'tokenType') && config.data.tokenType === TOKEN.ACCESS;

    // if token type is access token ,initiate transaction handler is required
    if (accessTokenExists && !has(config.handler, 'initiateTransaction')) {
      output.hasError = true;
      output.message = errorMessages.token.tokenHandler;
      return output;
    }

    // if token type is access token but no emiSubvention object is present throw error
    if (accessTokenExists && !has(config, 'emiSubvention')) {
      output.hasError = true;
      output.message = errorMessages.token.emiSubventionObject;
      return output;
    }

    // if referenceId key is not present in emiSubvention Object return error
    if (accessTokenExists && !has(config.emiSubvention, 'referenceId')) {
      output.hasError = true;
      output.message = errorMessages.token.referenceId;
      return output;
    }

    // if strategy key is not present in emiSubvention Object return error
    if (accessTokenExists && !has(config.emiSubvention, 'strategy')) {
      output.hasError = true;
      output.message = errorMessages.token.strategyRequired;
      return output;
    }
    const strategy = config && config.emiSubvention && config.emiSubvention.strategy;
    // if strategy is not item or amount based return error
    if (
      accessTokenExists &&
      (strategy !== EMI_SUBVENTION_STRATEGY.AMOUNT_BASED &&
        strategy !== EMI_SUBVENTION_STRATEGY.ITEM_BASED)
    ) {
      output.hasError = true;
      output.message = errorMessages.token.strategyType;
      return output;
    }

    const amountKeyExists =
      (config && config.emiSubvention && has(config.emiSubvention, 'subventionAmount')) || false;
    if (
      accessTokenExists &&
      strategy === EMI_SUBVENTION_STRATEGY.AMOUNT_BASED &&
      !amountKeyExists
    ) {
      output.hasError = true;
      output.message = errorMessages.token.noAmount;
      return output;
    }

    const items = (config && config.emiSubvention && config.emiSubvention.items) || [];
    if (accessTokenExists && strategy === EMI_SUBVENTION_STRATEGY.ITEM_BASED && !items.length) {
      output.hasError = true;
      output.message = errorMessages.token.noItems;
      return output;
    }
    if (accessTokenExists && strategy === EMI_SUBVENTION_STRATEGY.ITEM_BASED && items.length) {
      /* object values is not supported on old browsers so changing that to object.keys */
      Object.keys(items).forEach(key => {
        const item = items[key];
        if (
          !(
            item[EMI_SUBVENTION_ITEM_KEYS.BRAND_ID] ||
            item[EMI_SUBVENTION_ITEM_KEYS.CATEGORY_LIST] ||
            item[EMI_SUBVENTION_ITEM_KEYS.MODEL] ||
            item[EMI_SUBVENTION_ITEM_KEYS.EAN]
          )
        ) {
          output.hasError = true;
          output.message = errorMessages.token.itemsKey;
        }
      });

      return output;
    }

    return output;
  }
};

const isValid = config => {
  let output = validations.merchant(config);

  if (output.hasError) {
    return output;
  }

  output = validations.handler(config);

  if (output.hasError) {
    return output;
  }

  output = validations.data(config);
  if (output.hasError) {
    return output;
  }
  output = validations.flow(config);
  if (output.hasError) {
    return output;
  }
  output = validations.token(config);
  if (output.hasError) {
    return output;
  }

  return output; // merchant configuration is valid
};

const resetConfig = c => {
  const config = c;
  config.labels = {};
  config.payMode = {
    labels: {},
    filter: [],
    order: []
  };
  return config;
};
const init = () => {
  _config = resetConfig(_config); // reset configuration to empty
};

init();
export const get = () => _config;

export const set = (config, validate = false) => {
  const c = mergeObject(_config, config);
  if (validate) {
    const valid = isValid(c);
    if (valid.hasError) {
      log({ eventType: EVENT.MERCHANT_CONFIG_ERROR, config: c, error: valid });
      throw new Error(valid.message);
    }
  }
  _config = c;
  return _config;
};



// WEBPACK FOOTER //
// ./common/merchant-config.js